import React, { ElementType } from 'react';
import { connect, MapStateToProps, MapDispatchToProps } from 'react-redux';
import styled from 'styled-components';

// types
import { OvpMetadata } from 'pages/MerchantPages/Assets/assetTypes';
import RootState from 'ducks/RootState';
import Rules from 'restrictions/roles/constants/rules';
import { ItemDetails } from 'ducks/merchantDucks/items/types';

// hooks
import useRestrictionRules from 'restrictions/roles/hooks/useRestrictionRules';
import useSharedBoolean from 'pages/MerchantPages/Assets/containers/EditAsset/hooks/useSharedBoolean';

// actions
import { updateAssetContent as updateAssetContentAction } from 'ducks/merchantDucks/items/actions';

// components
import AccordionFooter from 'components/AccordionFooter';
import ContentTypeMorpher from './ContentTypeMorpher';

// OVP content components
import BrightcoveContainer from '../../components/BrightcoveContent';
import CloudFrontContent from '../../components/CloudFrontContent';
import DaCastContent from '../../components/DaCastContent';
import JWPlayerContent from '../../components/JwPlayer';
import KalturaContent from '../../components/KalturaContent';
import LaolaContent from '../../components/LaolaContent';
import LiveStreamContent from '../../components/LiveStreamContent';
import MediaspaceContent from '../../components/MediaspaceContent';
import OoyalaContent from '../../components/OoyalaContent';
import PanoptoContent from '../../components/PanoptoContent';
import PikselContent from '../../components/PikselContent';
import QbrickContainer from '../../components/QbrickContent';
import SportRadarContent from '../../components/SportRadarContent';
import WistiaContent from '../../components/WistiaContent';
import WowzaContent from '../../components/WowzaContent';
import SportOneContent from '../../components/SportOneContent';
import StreamAMGContent from '../../components/StreamAMGContent';
import VidyardContent from '../../components/VidyardContent';
import CloudflareContent from '../../components/CloudflareContent';
import FlowPlayerContent from '../../components/FlowPlayerContent';
import IvsContent from '../../components/IvsContent';

// HTML content
import HtmlContent from '../../components/HtmlContent';

// Dlc content
import DlcContent from '../../components/DlcContent';

// RSS content
import RssContent from '../../components/RssContent';
import AccedoContent from '../../components/AccedoContent';

const ovpContents: { [key: string]: ElementType } = {
  // ovp
  accedo: AccedoContent,
  brightcove: BrightcoveContainer,
  cloudfront: CloudFrontContent,
  dacast: DaCastContent,
  jw: JWPlayerContent,
  kaltura: KalturaContent,
  kaltura_mediaspace: MediaspaceContent,
  laola: LaolaContent,
  livestream: LiveStreamContent,
  ooyala: OoyalaContent,
  panopto: PanoptoContent,
  piksel: PikselContent,
  qbrick: QbrickContainer,
  sportradar: SportRadarContent,
  wistia: WistiaContent,
  wowza: WowzaContent,
  sport1: SportOneContent,
  streamamg: StreamAMGContent,
  vidyard: VidyardContent,
  cloudflare: CloudflareContent,
  flowplayer: FlowPlayerContent,
  ivs: IvsContent,

  // html
  html: HtmlContent,

  // dlc
  dlc: DlcContent,

  // rss
  rss: RssContent,
};

const AssetContentWrapper = styled.div`
  display: grid;
  height: 100%;
`;

interface StateProps {
  item: ItemDetails;
}

interface DispatchProps {
  updateAssetContent: (
    content: string,
    externalAssetId?: string,
    streamURL?: string,
    source?: any,
    ovpMetadata?: OvpMetadata
  ) => void;
}

interface OwnProps {
  closePanel: () => void;
  accordionLabel: string;
}

type Props = StateProps & DispatchProps & OwnProps;

const AssetContent = ({
  item,
  item: {
    item_type: { name: assetType },
    content: rawContent,
  },
  updateAssetContent,
  closePanel,
  accordionLabel,
}: Props) => {
  const ovpName = assetType.replace(/_asset$/, '');
  const OvpContentComponent = ovpContents[ovpName];

  const { hasRule } = useRestrictionRules();
  const isReadOnly = hasRule(Rules.READONLY_ASSETS_AND_PACKAGES);
  const [isSaveButtonDisabled] = useSharedBoolean('isSaveButtonDisabled', false);

  const onSubmit = async (
    assetContent: string,
    externalAssetId?: string,
    streamURL?: string,
    assetMetadata?: OvpMetadata
  ) => {
    await updateAssetContent(assetContent, externalAssetId, streamURL, null, assetMetadata);
    closePanel();
  };

  return (
    <AssetContentWrapper>
      <ContentTypeMorpher item={item} />
      {OvpContentComponent && (
        <OvpContentComponent
          formId="asset-content"
          rawContent={rawContent}
          onSubmit={onSubmit}
          isReadOnly={isReadOnly}
          closePanel={closePanel}
        />
      )}
      <AccordionFooter
        accordionLabel={accordionLabel}
        form="asset-content"
        handleCancel={closePanel}
        disabled={isSaveButtonDisabled}
      />
    </AssetContentWrapper>
  );
};

export const mapStateToProps: MapStateToProps<StateProps, OwnProps, RootState> = (state) => ({
  item: state.items.itemDetails,
});

export const mapDispatchToProps: MapDispatchToProps<DispatchProps, OwnProps> = {
  updateAssetContent: updateAssetContentAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(AssetContent);
